import { Link } from 'react-router-dom';
import Footer from '@/components/feature/Footer';
import Navigation from '@/components/feature/Navigation';
import { serverEndpoints } from '@/config/serverEndpoints';
import CopyableIp from '@/components/base/CopyableIp';

export default function DownloadPage() {
  const buildFingerprint = '2026-05-12-r2-content-packs-retired';
  const serverAddress = serverEndpoints.direct.address;
  const steamConnectUrl = `steam://connect/${serverAddress}`;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navigation />
      <main
        className="relative flex-1 overflow-hidden pt-24 pb-16"
        data-build-fingerprint={buildFingerprint}
      >
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              'radial-gradient(circle at top, rgba(139,0,0,0.22), transparent 35%), radial-gradient(circle at 20% 80%, rgba(220,38,38,0.12), transparent 28%), linear-gradient(180deg, #040404 0%, #090909 50%, #020202 100%)',
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:100%_6px] opacity-20 pointer-events-none" />

        <div className="container relative z-10 mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex items-center gap-3 text-sm font-orbitron uppercase tracking-[0.28em] text-gray-400">
              <i className="ri-skull-fill text-red-500 text-lg"></i>
              <Link to="/" className="transition-colors hover:text-red-400">
                Uliunai
              </Link>
              <span className="text-red-900">/</span>
              <span className="text-white">Join</span>
            </div>

            <section className="relative overflow-hidden rounded-[2rem] border border-red-900/40 bg-gradient-to-br from-black via-[#110303] to-black px-6 py-8 shadow-2xl shadow-red-950/30 md:px-10 md:py-12">
              <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 14% 18%, rgba(220,38,38,0.28), transparent 25%), radial-gradient(circle at 82% 12%, rgba(220,38,38,0.14), transparent 22%), linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)' }} />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-red-800/50 bg-red-950/30 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-red-300">
                  <i className="ri-flashlight-line"></i>
                  Fast Download
                </div>

                <div className="mt-6 grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
                  <div>
                    <h1 className="max-w-3xl font-orbitron text-4xl font-black leading-tight text-white md:text-6xl">
                      Join the <span className="text-red-500">Uliunai KF1</span> server.
                    </h1>
                    <p className="mt-5 max-w-2xl text-lg leading-relaxed text-gray-300">
                      Content packs are retired. The server now serves required custom files through the in-game fast-download redirect when you connect.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3 text-sm text-gray-400">
                      <span className="rounded-full border border-red-900/40 bg-black/40 px-4 py-2">
                        <i className="ri-server-line mr-2 text-red-400"></i>
                        Direct KF1 server
                      </span>
                      <span className="rounded-full border border-red-900/40 bg-black/40 px-4 py-2">
                        <i className="ri-speed-up-line mr-2 text-red-400"></i>
                        Automatic redirect downloads
                      </span>
                    </div>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <a
                        href={steamConnectUrl}
                        className="inline-flex items-center justify-center gap-3 rounded-2xl bg-red-600 px-5 py-4 font-orbitron text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-red-500"
                      >
                        <i className="ri-gamepad-line"></i>
                        Join Server
                      </a>
                      <CopyableIp className="justify-center rounded-2xl border border-red-900/40 bg-black/40 px-5 py-4 text-sm text-red-300 hover:border-red-700/60 hover:text-red-200" />
                    </div>
                  </div>

                  <div className="rounded-3xl border border-red-900/30 bg-black/35 p-6 backdrop-blur-sm">
                    <p className="font-orbitron text-xs uppercase tracking-[0.28em] text-red-300">
                      What Changed
                    </p>
                    <p className="mt-4 text-gray-300">
                      The separate maps and mutators zip downloads were removed to avoid keeping huge duplicate archives online.
                    </p>
                    <div className="mt-6 space-y-3">
                      <div className="rounded-2xl border border-red-900/25 bg-[#0a0a0a] p-4">
                        <p className="text-sm font-semibold text-white">Server Address</p>
                        <CopyableIp className="mt-1 text-red-300 hover:text-red-200" />
                      </div>
                      <div className="rounded-2xl border border-red-900/25 bg-[#0a0a0a] p-4">
                        <p className="text-sm font-semibold text-white">Content Delivery</p>
                        <p className="mt-1 text-sm text-gray-400">KF1 downloads missing custom files from the server redirect during first join.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-8 grid gap-6 lg:grid-cols-2">
              <article className="rounded-[1.6rem] border border-red-900/35 bg-[#0b0b0b] p-6 shadow-xl shadow-black/30">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-orbitron text-xl font-bold uppercase tracking-[0.14em] text-white">No Pre-download Required</p>
                    <p className="mt-3 max-w-md text-gray-400">
                      Start the game, join the server, and let the redirect fetch only the files your install is missing.
                    </p>
                  </div>
                  <i className="ri-download-cloud-line text-3xl text-red-500"></i>
                </div>
              </article>

              <article className="rounded-[1.6rem] border border-red-900/35 bg-[#0b0b0b] p-6 shadow-xl shadow-black/30">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-orbitron text-xl font-bold uppercase tracking-[0.14em] text-white">Legacy Pack Links Retired</p>
                    <p className="mt-3 max-w-md text-gray-400">
                      Old CDN zip archives are no longer maintained. Reconnect normally if KF1 asks for server content.
                    </p>
                  </div>
                  <i className="ri-folder-forbid-line text-3xl text-red-500"></i>
                </div>
              </article>
            </section>

            <section className="mt-8 grid gap-6 lg:grid-cols-[1fr_0.9fr]">
              <div className="rounded-[1.6rem] border border-red-900/35 bg-[#090909] p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <i className="ri-list-ordered text-2xl text-red-500"></i>
                  <h2 className="font-orbitron text-2xl font-bold text-white">Join Steps</h2>
                </div>
                <ol className="mt-6 space-y-4">
                  {[
                    'Launch Killing Floor from Steam.',
                    'Open the server browser or use the direct connect command.',
                    `Connect to ${serverAddress}.`,
                    'Let the game download any missing custom files from the fast-download redirect.',
                    'Reconnect after the first download pass if the game asks for it.',
                  ].map((step, index) => (
                    <li key={step} className="flex gap-4 rounded-2xl border border-red-900/25 bg-black/40 p-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-700 font-orbitron font-bold text-white">
                        {index + 1}
                      </div>
                      <p className="pt-1 text-gray-300">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-[1.6rem] border border-red-900/35 bg-[#090909] p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <i className="ri-question-line text-2xl text-red-500"></i>
                  <h2 className="font-orbitron text-2xl font-bold text-white">Troubleshooting</h2>
                </div>
                <div className="mt-6 space-y-4">
                  {[
                    ['The game downloads files on first join', 'That is expected. The old full zip packs were retired, so KF1 pulls missing files during connection.'],
                    ['The connection times out during download', 'Reconnect once. Large custom assets can require a second join after KF1 finishes writing files.'],
                    ["I can't find the server", `Use direct connect with ${serverAddress} or add it to favorites in the server browser.`],
                    ['A file keeps failing', 'Restart the game and retry. If it still fails, report the exact filename in Discord or WebAdmin logs.'],
                  ].map(([title, body]) => (
                    <details key={title} className="group rounded-2xl border border-red-900/25 bg-black/40 p-4">
                      <summary className="cursor-pointer list-none font-orbitron text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors group-open:text-red-400">
                        {title}
                      </summary>
                      <p className="mt-3 text-sm leading-relaxed text-gray-400">{body}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
